import { useState, useEffect } from 'react';

/**
 * Custom hook to manage subscription popup visibility
 * Features:
 * - Auto-show after delay
 * - Show on exit intent
 * - Respect user's choice (don't show again if dismissed)
 * - LocalStorage persistence
 */
export const useSubscriptionPopup = (options = {}) => {
    const {
        autoShowDelay = 10000, // Show after 10 seconds
        enableExitIntent = true,
        storageKey = 'goodikids_subscription_popup'
    } = options;

    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        // Check if user has already dismissed or subscribed
        const dismissed = localStorage.getItem(storageKey);
        if (dismissed) return;

        // Auto-show after delay
        const timer = setTimeout(() => {
            setIsOpen(true);
        }, autoShowDelay);

        // Exit intent detection
        const handleMouseLeave = (e) => {
            if (enableExitIntent && e.clientY <= 0 && !dismissed) {
                setIsOpen(true);
            }
        };

        if (enableExitIntent) {
            document.addEventListener('mouseleave', handleMouseLeave);
        }

        return () => {
            clearTimeout(timer);
            if (enableExitIntent) {
                document.removeEventListener('mouseleave', handleMouseLeave);
            }
        };
    }, [autoShowDelay, enableExitIntent, storageKey]);

    const openPopup = () => setIsOpen(true);

    const closePopup = () => {
        setIsOpen(false);
        // Remember user dismissed it (expires in 7 days)
        const expiryDate = new Date();
        expiryDate.setDate(expiryDate.getDate() + 7);
        localStorage.setItem(storageKey, JSON.stringify({
            dismissed: true,
            expiresAt: expiryDate.toISOString()
        }));
    };

    const resetPopup = () => {
        localStorage.removeItem(storageKey);
        setIsOpen(false);
    };

    return {
        isOpen,
        openPopup,
        closePopup,
        resetPopup
    };
};
